import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Award, Users, Stethoscope, HeartPulse, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const values = [
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We treat every patient with empathy, respect, and genuine care for their well-being.',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'Honest, ethical practice guides every decision we make for our patients.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We pursue the highest clinical standards and continuously advance our skills.',
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We work alongside you as partners in your health, not just providers.',
  },
];

const team = [
  {
    name: 'Dr. Elizabeth Morgan, MD',
    role: 'Chief Medical Officer',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=500&q=80',
    bio: 'Board-certified internist with 25+ years of clinical experience in general medicine and preventive care.',
    specialty: 'Internal Medicine',
  },
  {
    name: 'Dr. James Chen, MD',
    role: 'Lead Surgeon',
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=500&q=80',
    bio: 'Board-certified surgeon specializing in minor surgical procedures and wound management.',
    specialty: 'General Surgery',
  },
  {
    name: 'Dr. Amanda Foster, MD',
    role: 'Primary Care Physician',
    image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=500&q=80',
    bio: 'Family medicine physician dedicated to comprehensive preventive care and chronic disease management.',
    specialty: 'Family Medicine',
  },
  {
    name: 'Dr. Marcus Williams, MD',
    role: 'Physician & Specialist',
    image: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=500&q=80',
    bio: 'Experienced physician focused on diagnostic excellence and personalized treatment plans.',
    specialty: 'Internal Medicine',
  },
];

const milestones = [
  { year: '2009', title: 'Founded', description: 'HealthCheck Medical opened its doors to serve the community with quality primary care.' },
  { year: '2014', title: 'Expanded Services', description: 'Added surgical procedures and specialized diagnostic services.' },
  { year: '2018', title: 'New Facility', description: 'Moved into our modern medical center with advanced equipment.' },
  { year: '2022', title: 'Second Location', description: 'Expanded to serve more patients across multiple locations.' },
  { year: '2024', title: 'Today', description: 'Caring for 25,000+ patients annually with comprehensive medical services.' },
];

export const About = () => {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 bg-navy" data-testid="about-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1504813184591-01572f98c85f?w=1920&q=80"
            alt="Medical team"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              About Us
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Dedicated to Your Health Since 2009
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed">
              HealthCheck Medical Inc. is a trusted medical care center providing comprehensive medical and surgical services with a commitment to safe, professional, and compassionate healthcare.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80"
                alt="Doctor caring for patient"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-emerald p-6 hidden md:block">
                <Stethoscope className="w-12 h-12 text-white mb-2" />
                <div className="text-white font-semibold">Trusted Care</div>
              </div>
            </div>
            
            <div>
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-8 h-8 text-emerald" />
                  <h2 className="font-manrope font-bold text-2xl text-navy">Our Mission</h2>
                </div>
                <p className="text-navy-600 leading-relaxed">
                  To deliver safe, professional, and compassionate healthcare tailored to each patient's needs. We focus on accurate diagnosis, effective treatment, patient education, and continuous support to improve health, restore wellness, and enhance quality of life.
                </p>
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-8 h-8 text-emerald" />
                  <h2 className="font-manrope font-bold text-2xl text-navy">Our Vision</h2>
                </div>
                <p className="text-navy-600 leading-relaxed">
                  To be the most trusted medical care center in our community—known for clinical excellence, genuine compassion, and empowering every patient to achieve their best health.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-navy-50" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Our Values
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              The Principles That Guide Our Care
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <Card 
                key={value.title}
                className="bg-white border-silver-light shadow-card card-hover"
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-sm bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-emerald" />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{value.title}</h3>
                  <p className="text-sm text-navy-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-white" data-testid="timeline-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Our Journey
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              15 Years of Caring
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-silver-light hidden md:block"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={milestone.year}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  data-testid={`milestone-${milestone.year}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white p-6 border border-silver-light shadow-card">
                      <div className="stat-value text-2xl font-bold text-emerald mb-2">{milestone.year}</div>
                      <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{milestone.title}</h3>
                      <p className="text-sm text-navy-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-emerald border-4 border-emerald-100 z-10"></div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-navy-50" data-testid="team-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Our Team
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              Meet Our Doctors & Surgeons
            </h2>
            <p className="text-navy-600 mt-4">
              Board-certified physicians dedicated to delivering exceptional care.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <Card 
                key={member.name}
                className="bg-white border-silver-light shadow-card overflow-hidden group"
                data-testid={`team-${member.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              >
                <div className="relative overflow-hidden h-64">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy to-transparent p-4">
                    <div className="text-xs text-emerald-100 font-semibold uppercase tracking-wider">{member.specialty}</div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-manrope font-semibold text-navy text-lg">{member.name}</h3>
                  <p className="text-emerald text-sm font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-navy-600">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-emerald" data-testid="about-stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '25K+', label: 'Patients Served' },
              { value: '15+', label: 'Years of Care' },
              { value: '20+', label: 'Specialist Doctors' },
              { value: '98%', label: 'Satisfaction Rate' },
            ].map((stat) => (
              <div key={stat.label} data-testid={`about-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="stat-value text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-sm text-emerald-100 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-navy" data-testid="about-cta-section">
        <div className="container-custom text-center">
          <HeartPulse className="w-14 h-14 text-emerald mx-auto mb-6" />
          <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Experience Care That Puts You First
          </h2>
          <p className="text-navy-200 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of patients who trust HealthCheck Medical for their healthcare needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
              data-testid="about-cta-contact"
            >
              <Link to="/contact">
                Book Appointment
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-navy rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8 bg-transparent"
              data-testid="about-cta-services"
            >
              <Link to="/services">Our Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
